import React from "react";
import { plans, items } from "../PricingSectionCommon";
import { TEAM_MAX_SEATS } from "../../util/prices";

function CheckoutSummary({ plan }) {
  // Parse plan string (format: "qty-planId" or just "planId"). The leading
  // number is ignored: checkout always buys one unit and every plan includes
  // ten seats (decision, Ben, 2026-09-22).
  // Examples: "1-sbm" → sbm | "sbm" → sbm
  const planParts = plan ? plan.split("-") : [];
  const productId = planParts.length > 1 ? planParts.slice(1).join("-") : planParts[0];

  // Look up plan data from the same source as pricing page
  const planData = plans.find(p => p.id === productId) || items.find(i => i.id === productId);

  // If no plan found, show error
  if (!planData) {
    return (
      <div className="bg-white rounded-lg shadow p-6 sticky top-4">
        <h2 className="text-xl font-bold mb-4">Order Summary</h2>
        <p className="text-gray-500">
          {productId ? "Unknown product" : "Select a product to see pricing"}
        </p>
      </div>
    );
  }

  // Parse price (remove commas if present)
  const priceString = String(planData.price).replace(/,/g, "");
  const unitPrice = parseFloat(priceString);
  const totalPrice = unitPrice;

  // Format price for display
  const formatPrice = (amount) => {
    if (isNaN(amount)) return "Price not available";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Get billing period text
  const getBillingText = () => {
    if (planData.period === "one time") return " one-time";
    if (planData.period === "per month") return "/month";
    if (planData.period === "per year") return "/year";
    return "";
  };

  const billingText = getBillingText();

  return (
    <div className="bg-white rounded-lg shadow p-6 sticky top-4">
      <h2 className="text-xl font-bold mb-4">Order Summary</h2>

      <div className="space-y-4">
        <div>
          <h3 className="font-semibold text-gray-900">{planData.name}</h3>
        </div>

        <div className="border-t pt-4">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Price:</span>
            <span className="font-medium">
              {formatPrice(unitPrice)}{billingText}
            </span>
          </div>
          <div className="flex justify-between text-sm mt-2">
            <span className="text-gray-600">Seats:</span>
            <span className="font-medium">{TEAM_MAX_SEATS}</span>
          </div>
        </div>

        <div className="border-t pt-4">
          <div className="flex justify-between">
            <span className="text-lg font-semibold">Total:</span>
            <span className="text-lg font-semibold text-green-600">
              {formatPrice(totalPrice)}{billingText}
            </span>
          </div>
          {planData.period === "per month" && (
            <p className="text-xs text-gray-500 mt-2">
              Cancel anytime. No long-term commitment.
            </p>
          )}
          {planData.period === "per year" && (
            <p className="text-xs text-gray-500 mt-2">
              Billed annually. Save compared to monthly!
            </p>
          )}
        </div>

        <div className="bg-gray-50 rounded p-3 mt-4">
          <p className="text-xs text-gray-600">
            Secure payment powered by Stripe
          </p>
        </div>
      </div>
    </div>
  );
}

export default CheckoutSummary;
